import { useState, useEffect, useContext } from "react";
import { getApiUrl } from "../utils/api";
import { Link, useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import { useToast } from "../context/ToastContext";
import Modal from "../components/Modal";
import CoverImageCropModal from "../components/CoverImageCropModal";
import UploadProgressBar from "../components/UploadProgressBar";
import { getAvatarUrl, DEFAULT_AVATAR_DATA_URL, FALLBACK_AVATAR_SVG } from "../utils/avatar";
import { isAllowedImageType, readFileAsDataUrl } from "../utils/imageCrop";
import { uploadFormData } from "../utils/uploadWithProgress";
import {
  Editor,
  EditorState,
  convertToRaw,
  RichUtils,
  SelectionState,
} from "draft-js";
import "draft-js/dist/Draft.css";
import {
  FaAlignLeft,
  FaAlignCenter,
  FaAlignRight,
  FaAlignJustify,
  FaTimes,
  FaPen,
  FaPenNib,
  FaImage,
  FaUsers,
} from "react-icons/fa";
import { poemSchemas } from "../validation/schemas";
import { validateForm, sanitizeFormData } from "../validation/validate";

export default function WritePoem() {
  const navigate = useNavigate();
  const location = useLocation();
  const { groupId: routeGroupId } = useParams();
  const [searchParams] = useSearchParams();
  const groupId = routeGroupId || searchParams.get("group") || "";
  const { user, userLoading } = useContext(UserContext);
  const toast = useToast();
  const [groupInfo, setGroupInfo] = useState(null);
  const [groupAccessError, setGroupAccessError] = useState("");
  const [form, setForm] = useState({
    title: "",
    content: "",
    alignment: "left",
    tags: "",
    copyrightEnabled: true,
  });
  const [formErrors, setFormErrors] = useState({});
  const [editorState, setEditorState] = useState(() =>
    EditorState.createEmpty()
  );
  const [modal, setModal] = useState({
    open: false,
    title: "",
    message: "",
    type: "info",
  });
  const [editorFocused, setEditorFocused] = useState(false);
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const [tagInputFocused, setTagInputFocused] = useState(false);
  const [inviteUsername, setInviteUsername] = useState("");
  const [pendingInvites, setPendingInvites] = useState([]);
  const [collabSuggestions, setCollabSuggestions] = useState([]);
  const [showCollabSuggestions, setShowCollabSuggestions] = useState(false);
  const [collabDebounce, setCollabDebounce] = useState(null);
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [coverImagePreview, setCoverImagePreview] = useState(null);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [isCropOpen, setIsCropOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [editorMounted, setEditorMounted] = useState(false);

  useEffect(() => {
    if (userLoading || !user) {
      setEditorMounted(false);
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        setEditorMounted(true);
      });
    });

    return () => {
      window.cancelAnimationFrame(frameId);
      setEditorMounted(false);
    };
  }, [user, userLoading]);

  useEffect(() => {
    if (userLoading) return;
    if (!user) {
      const from = `${location.pathname}${location.search}`;
      navigate("/login", { state: { from }, replace: true });
    }
  }, [user, userLoading, navigate, location.pathname, location.search]);

  useEffect(() => {
    return () => {
      if (coverImagePreview) URL.revokeObjectURL(coverImagePreview);
    };
  }, [coverImagePreview]);

  useEffect(() => {
    if (!groupId) {
      setGroupInfo(null);
      setGroupAccessError("");
      return;
    }
    if (!user) {
      return;
    }

    let cancelled = false;
    (async () => {
      setGroupAccessError("");
      try {
        const res = await fetch(getApiUrl(`/api/groups/${groupId}`), {
          credentials: "include",
        });
        if (res.status === 404) {
          if (!cancelled) navigate("/404", { replace: true });
          return;
        }
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed to load group");
        if (!data.isMember) {
          if (!cancelled) {
            setGroupAccessError("Only group members can write poems here.");
            setGroupInfo({ _id: data._id, name: data.name });
          }
          return;
        }
        if (!cancelled) setGroupInfo({ _id: data._id, name: data.name });
      } catch {
        if (!cancelled) {
          setGroupAccessError("Could not load this group.");
          setGroupInfo(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [groupId, user, navigate]);

  const handleCoverImageChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!isAllowedImageType(file)) {
      setModal({
        open: true,
        title: "Invalid File",
        message:
          "Please choose a JPEG, PNG, GIF, or WebP image for your poem.",
        type: "error",
      });
      return;
    }

    try {
      const dataUrl = await readFileAsDataUrl(file);
      setCropImageSrc(dataUrl);
      setIsCropOpen(true);
    } catch {
      setModal({
        open: true,
        title: "Image Error",
        message: "Could not load that image. Please try another file.",
        type: "error",
      });
    }
  };

  const handleCoverCropComplete = (file, previewUrl) => {
    if (coverImagePreview) URL.revokeObjectURL(coverImagePreview);
    setCoverImageFile(file);
    setCoverImagePreview(previewUrl);
    setIsCropOpen(false);
    setCropImageSrc(null);
  };

  const handleCoverCropCancel = () => {
    setIsCropOpen(false);
    setCropImageSrc(null);
  };

  const handleRemoveCoverImage = () => {
    if (coverImagePreview) URL.revokeObjectURL(coverImagePreview);
    setCoverImageFile(null);
    setCoverImagePreview(null);
  };

  const postPoemFormData = async (data) => {
    setIsUploading(true);
    setUploadProgress(0);
    try {
      return await uploadFormData({
        url: getApiUrl("/api/poems"),
        method: "POST",
        body: data,
        onProgress: setUploadProgress,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: "" });
    }

    // Fetch tag suggestions when typing in tags field
    if (name === "tags" && value.trim()) {
      // Split by comma and get all parts
      const allParts = value.split(",").map((t) => t.trim());
      const lastPart = allParts[allParts.length - 1];

      // Count only complete tags (exclude the last part if it's still being typed)
      const completeTags = allParts.slice(0, -1).filter((t) => t.length > 0);

      // Only show suggestions if we have less than 5 complete tags
      if (completeTags.length < 5) {
        if (lastPart && lastPart.length > 0) {
          fetchTagSuggestions(lastPart);
        } else {
          setTagSuggestions([]);
          setShowTagSuggestions(false);
        }
      } else {
        setTagSuggestions([]);
        setShowTagSuggestions(false);
      }
    } else if (name === "tags" && !value.trim()) {
      setTagSuggestions([]);
      setShowTagSuggestions(false);
    }
  };

  // Fetch tag suggestions from trending tags
  const fetchTagSuggestions = async (query) => {
    try {
      const res = await fetch(
        getApiUrl(`/api/poems/tags/trending?limit=20`)
      );
      if (res.ok) {
        const tags = await res.json();
        const filtered = tags
          .filter((tagData) =>
            tagData.tag.toLowerCase().includes(query.toLowerCase())
          )
          .slice(0, 5);
        setTagSuggestions(filtered);
        setShowTagSuggestions(filtered.length > 0);
      }
    } catch (err) {
      console.error("Error fetching tag suggestions:", err);
    }
  };

  // Handle tag suggestion click
  const handleTagSuggestionClick = (tag) => {
    const currentTags = form.tags
      ? form.tags
          .split(",")
          .map((t) => t.trim())
          .filter((t) => t.length > 0)
      : [];

    // Check if we already have 5 complete tags
    if (currentTags.length >= 5) {
      setShowTagSuggestions(false);
      return;
    }

    if (!currentTags.includes(tag)) {
      // Replace the last incomplete tag with the selected tag
      const tagsArray = form.tags ? form.tags.split(",") : [];
      if (tagsArray.length > 0) {
        // Remove the last incomplete tag and add the selected one
        tagsArray[tagsArray.length - 1] = tag;
        const newTags = tagsArray.join(", ");
        setForm({ ...form, tags: newTags });
      } else {
        // If no tags yet, just add the selected tag
        setForm({ ...form, tags: tag });
      }
    }
    setShowTagSuggestions(false);
  };

  const handleSaveDraft = async (e) => {
    e.preventDefault();

    // Check if user has a username (pen name) - required for saving drafts
    if (!user?.username) {
      setModal({
        open: true,
        title: "Pen Name Required",
        message: "Please set your pen name in your profile before saving a draft.",
        type: "error",
        onConfirm: () => {
          navigate("/profile");
        },
        confirmText: "Go to Profile",
      });
      return;
    }

    // Check if at least title or content is provided
    const current = editorState.getCurrentContent();
    const plainText = current.getPlainText().trim();
    if (!form.title.trim() && !plainText) {
      setModal({
        open: true,
        title: "Error",
        message: "Please enter at least a title or content for your draft.",
        type: "error",
      });
      return;
    }

    // Check tag limit
    if (form.tags) {
      const currentTags = form.tags
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t);

      if (currentTags.length > 5) {
        setModal({
          open: true,
          title: "Too Many Tags",
          message:
            "You can add a maximum of 5 tags to your poem. Please remove some tags and try again.",
          type: "error",
        });
        return;
      }
    }

    // Get content from editor
    const alignedBlockMap = current.getBlockMap().map((block) => {
      const blockData = block.getData();
      const newData = blockData.set("textAlign", form.alignment);
      return block.set("data", newData);
    });
    const alignedContent = current.set("blockMap", alignedBlockMap);
    const rawContent = JSON.stringify(convertToRaw(alignedContent));

    const data = new FormData();
    data.append("title", form.title.trim() || "Untitled");
    data.append("alignment", form.alignment);
    data.append("font", "serif");
    data.append("content", rawContent);
    data.append("tags", form.tags || "");
    data.append("isDraft", "true");
    data.append("copyrightEnabled", String(!!form.copyrightEnabled));
    data.append("copyrightHolder", String(user?.username || "").trim());
    data.append("copyrightYear", String(new Date().getFullYear()));
    if (coverImageFile) data.append("coverImage", coverImageFile);

    const { ok, data: result } = await postPoemFormData(data);
    setUploadProgress(100);
    if (ok) {
      // Show toast notification
      toast.success("Draft saved successfully!", { title: "Success" });
      setTimeout(() => navigate("/profile"), 800);
    } else {
      // Check if username is required
      if (result.requiresUsername) {
        setModal({
          open: true,
          title: "Pen Name Required",
          message: result.error || "Please set your pen name in your profile before saving.",
          type: "error",
          onConfirm: () => {
            navigate("/profile");
          },
          confirmText: "Go to Profile",
        });
      } else {
        setModal({
          open: true,
          title: "Error",
          message: result.error || "Failed to save draft",
          type: "error",
        });
      }
    }
  };

  const sharePoemToGroup = async (poemId, targetGroupId) => {
    const shareRes = await fetch(
      getApiUrl(`/api/groups/${targetGroupId}/share`),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ poemId }),
      }
    );
    const shareData = await shareRes.json();
    if (!shareRes.ok) {
      throw new Error(shareData.error || "Failed to share poem to group");
    }
    window.dispatchEvent(
      new CustomEvent("groups-updated", {
        detail: { groupId: targetGroupId, delta: 1, skipFetch: true },
      })
    );
    window.dispatchEvent(
      new CustomEvent("group-new-post", { detail: { groupId: targetGroupId } })
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (groupId && groupAccessError) return;

    // Check if user has a username (pen name) - required for posting
    if (!user?.username) {
      setModal({
        open: true,
        title: "Pen Name Required",
        message: "Please set your pen name in your profile before posting a poem.",
        type: "error",
        onConfirm: () => {
          navigate("/profile");
        },
        confirmText: "Go to Profile",
      });
      return;
    }

    // Check title length and show toast if it exceeds 200 characters
    if (!form.title || form.title.trim().length === 0) {
      toast.error("Please enter a title for your poem.", {
        title: "Title Required",
      });
      return;
    }
    
    if (form.title.length > 200) {
      toast.warning(`Title is ${form.title.length} characters. Maximum allowed is 200 characters. Please shorten it before publishing.`, {
        title: "Title Too Long",
      });
      return;
    }

    // Get content from editor and check if it's actually empty
    const current = editorState.getCurrentContent();
    const plainText = current.getPlainText().trim();
    
    // Validate that poem has actual content (not just empty Draft.js structure)
    if (!plainText || plainText.length === 0) {
      setModal({
        open: true,
        title: "Content Required",
        message: "Please enter some content for your poem before posting.",
        type: "error",
      });
      setFormErrors({ ...formErrors, content: "Poem content is required" });
      return;
    }

    // Check tag limit before validation
    if (form.tags) {
      const currentTags = form.tags
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t);

      if (currentTags.length > 5) {
        setModal({
          open: true,
          title: "Too Many Tags",
          message:
            "You can add a maximum of 5 tags to your poem. Please remove some tags and try again.",
          type: "error",
        });
        return;
      }
    }

    // Get content from editor
    const alignedBlockMap = current.getBlockMap().map((block) => {
      const blockData = block.getData();
      const newData = blockData.set("textAlign", form.alignment);
      return block.set("data", newData);
    });
    const alignedContent = current.set("blockMap", alignedBlockMap);
    const rawContent = JSON.stringify(convertToRaw(alignedContent));

    // Prepare form data for validation
    const formData = {
      ...form,
      content: rawContent,
    };

    // Validate form data
    const validation = await validateForm(poemSchemas.createPoem, formData);
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }

    // Sanitize form data
    const sanitizedForm = sanitizeFormData(validation.data);

    const data = new FormData();
    data.append("title", sanitizedForm.title);
    data.append("alignment", sanitizedForm.alignment);
    data.append("font", "serif");
    data.append("content", rawContent);
    data.append("tags", sanitizedForm.tags || "");
    data.append("copyrightEnabled", String(!!sanitizedForm.copyrightEnabled));
    data.append("copyrightHolder", String(user?.username || "").trim());
    data.append("copyrightYear", String(new Date().getFullYear()));
    if (coverImageFile) data.append("coverImage", coverImageFile);
    if (groupId) data.append("groupOnly", "true");

    const { ok, data: result } = await postPoemFormData(data);
    setUploadProgress(100);
    if (ok) {
      // Send pending collaboration invites
      try {
        if (Array.isArray(pendingInvites) && pendingInvites.length > 0) {
          for (const uname of pendingInvites) {
            try {
              await fetch(getApiUrl(`/api/poems/${result._id}/collaborators/invite`), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ username: uname })
              });
            } catch {}
          }
        }
      } catch {}

      if (groupId) {
        try {
          await sharePoemToGroup(result._id, groupId);
          toast.success(
            `Poem published to ${groupInfo?.name || "the group"}!` +
              (pendingInvites.length ? " Invites sent." : ""),
            { title: "Success" }
          );
          setTimeout(() => navigate(`/groups/${groupId}`), 800);
        } catch (shareErr) {
          toast.warning(
            shareErr.message ||
              "Poem was published but could not be added to the group.",
            { title: "Partial success" }
          );
          setTimeout(() => navigate(`/poems/${result._id}`), 800);
        }
      } else {
        toast.success(
          "Poem posted successfully!" + (pendingInvites.length ? " Invites sent." : ""),
          { title: "Success" }
        );
        setTimeout(() => navigate("/"), 800);
      }
    } else {
      // Check if username is required
      if (result.requiresUsername) {
        setModal({
          open: true,
          title: "Pen Name Required",
          message: result.error || "Please set your pen name in your profile before posting.",
          type: "error",
          onConfirm: () => {
            navigate("/profile");
          },
          confirmText: "Go to Profile",
        });
      } else {
        setModal({
          open: true,
          title: "Error",
          message: result.error || "Something went wrong",
          type: "error",
        });
      }
    }
  };

  // Toolbar handlers
  const handleKeyCommand = (command, editorState) => {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      setEditorState(newState);
      return "handled";
    }
    return "not-handled";
  };

  const onFormatClick = (style) => {
    setEditorState(RichUtils.toggleInlineStyle(editorState, style));
  };

  function getAlignmentState(align) {
    return form.alignment === align ? "active" : "inactive";
  }

  // Helper to check if a style is active or mixed
  function getInlineStyleState(style) {
    if (!editorFocused) return "inactive";
    const selection = editorState.getSelection();
    const content = editorState.getCurrentContent();
    if (selection.isCollapsed()) {
      return editorState.getCurrentInlineStyle().has(style)
        ? "active"
        : "inactive";
    }
    const start = selection.getStartOffset();
    const end = selection.getEndOffset();
    const block = content.getBlockForKey(selection.getStartKey());
    let hasStyle = false;
    let noStyle = false;
    for (let i = start; i < end; i++) {
      if (block.getInlineStyleAt(i).has(style)) {
        hasStyle = true;
      } else {
        noStyle = true;
      }
    }
    if (hasStyle && noStyle) return "mixed";
    if (hasStyle) return "active";
    return "inactive";
  }

  return (
    <div className="write-poem-page poem-compose-page min-h-screen w-full max-w-full box-border overflow-x-hidden p-0 sm:p-8">
      <Modal
        open={modal.open}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        onClose={() => setModal({ ...modal, open: false })}
      />
      {userLoading || !user ? (
        <p className="text-[#777777] py-12">Loading...</p>
      ) : (
      <form
        onSubmit={handleSubmit}
        className="poem-compose peer-home-page-card p-4 sm:p-8 w-full max-w-none sm:max-w-2xl sm:mx-auto space-y-6 min-w-0 box-border"
      >
        <div className="mb-4">
          {groupInfo && (
            <div className="mb-4 rounded border border-[#ffd4bc] bg-[#fff5ef] px-4 py-3 text-sm text-[#333333]">
              <div className="flex items-center gap-2 font-semibold text-[#f26522]">
                <FaUsers className="w-4 h-4 shrink-0" aria-hidden="true" />
                Writing for {groupInfo.name}
              </div>
              <p className="mt-1 text-[#777777]">
                Your poem will be published and shared with this group.
              </p>
              <Link
                to={`/groups/${groupInfo._id}`}
                className="inline-block mt-2 text-[#f26522] font-semibold hover:underline"
              >
                Back to group
              </Link>
            </div>
          )}
          {groupAccessError && (
            <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              {groupAccessError}
              {groupInfo && (
                <Link
                  to={`/groups/${groupInfo._id}`}
                  className="block mt-2 font-semibold text-[#f26522] hover:underline"
                >
                  View group
                </Link>
              )}
            </div>
          )}
          <h2
            className="text-2xl sm:text-3xl font-bold mb-2"
            style={{ fontFamily: "var(--font-peer-serif)", letterSpacing: "-0.02em" }}
          >
            {groupInfo ? "Write for group" : "Write a Poem"}
          </h2>
          <p className="text-sm text-[#777777] mb-4">
            {groupInfo
              ? "Share a new poem with this group."
              : "Draft freely, then publish when it feels ready."}
          </p>
          <div className="flex flex-wrap items-center gap-4">
            {/* Alignment Controls */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Align</span>
              <div className="inline-flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, alignment: "left" })}
                  className={`w-9 h-9 flex items-center justify-center border rounded-md ${
                    getAlignmentState("left") === "active"
                      ? "bg-[#f26522] text-white border-[#f26522]"
                      : "bg-white text-[#333333] hover:bg-[#fff5ef] border border-[#eeeeee]"
                  }`}
                  title="Align left"
                  aria-label="Align left"
                >
                  <FaAlignLeft size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, alignment: "center" })}
                  className={`w-9 h-9 flex items-center justify-center border rounded-md ${
                    getAlignmentState("center") === "active"
                      ? "bg-[#f26522] text-white border-[#f26522]"
                      : "bg-white text-[#333333] hover:bg-[#fff5ef] border border-[#eeeeee]"
                  }`}
                  title="Align center"
                  aria-label="Align center"
                >
                  <FaAlignCenter size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, alignment: "right" })}
                  className={`w-9 h-9 flex items-center justify-center border rounded-md ${
                    getAlignmentState("right") === "active"
                      ? "bg-[#f26522] text-white border-[#f26522]"
                      : "bg-white text-[#333333] hover:bg-[#fff5ef] border border-[#eeeeee]"
                  }`}
                  title="Align right"
                  aria-label="Align right"
                >
                  <FaAlignRight size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, alignment: "justify" })}
                  className={`w-9 h-9 flex items-center justify-center border rounded-md ${
                    getAlignmentState("justify") === "active"
                      ? "bg-[#f26522] text-white border-[#f26522]"
                      : "bg-white text-[#333333] hover:bg-[#fff5ef] border border-[#eeeeee]"
                  }`}
                  title="Justify"
                  aria-label="Justify"
                >
                  <FaAlignJustify size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
        <div>
          <label className="block font-medium mb-1">Poem Title</label>
          <input
            type="text"
            name="title"
            placeholder="Poem Title"
            value={form.title}
            onChange={handleChange}
            className={`w-full min-w-0 max-w-full box-border p-2 border rounded break-words font-serif ${
              form.alignment === "center"
                ? "text-center"
                : form.alignment === "right"
                ? "text-right"
                : form.alignment === "justify"
                ? "text-justify"
                : "text-left"
            } ${formErrors.title ? "border-red-500" : form.title && form.title.length > 200 ? "border-orange-500" : ""}`}
            required
          />
          {formErrors.title && (
            <p className="text-red-500 text-xs mt-1">{formErrors.title}</p>
          )}
          {form.title && form.title.length > 200 && (
            <p className="text-orange-600 text-xs mt-1">
              Title is {form.title.length} characters. Maximum allowed is 200 characters. ({form.title.length - 200} over limit)
            </p>
          )}
        </div>
        <div>
          <label className="block font-medium mb-1">Picture (optional)</label>
          <div className="flex flex-wrap items-start gap-4">
            {coverImagePreview ? (
              <div className="relative">
                <img
                  src={coverImagePreview}
                  alt="Poem preview"
                  className="w-full max-w-xs h-40 object-cover rounded border border-[#eeeeee]"
                />
                <button
                  type="button"
                  onClick={handleRemoveCoverImage}
                  className="absolute top-2 right-2 bg-white/90 border border-[#eeeeee] rounded-full p-1 text-[#f26522] hover:bg-white"
                  aria-label="Remove image"
                >
                  <FaTimes size={14} />
                </button>
              </div>
            ) : (
              <label className="inline-flex items-center gap-2 px-4 py-2 border border-[#eeeeee] rounded-md bg-white text-[#f26522] hover:bg-[#fff5ef] cursor-pointer">
                <FaImage />
                <span>Add a picture</span>
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={handleCoverImageChange}
                  className="hidden"
                />
              </label>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            JPEG, PNG, GIF, or WebP. You can crop before uploading. Max 5MB.
          </p>
        </div>
        <div>
          <label className="block font-medium mb-1">Poem Content</label>
          <div className="mb-2 flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => onFormatClick("BOLD")}
              className={`px-2 py-1 border rounded font-bold ${
                getInlineStyleState("BOLD") === "active"
                  ? "bg-[#f26522] text-white"
                  : getInlineStyleState("BOLD") === "mixed"
                  ? "bg-yellow-300 text-black"
                  : "bg-gray-100 hover:bg-gray-200"
              }`}
              aria-label="Bold"
            >
              B
            </button>
            <button
              type="button"
              onClick={() => onFormatClick("ITALIC")}
              className={`px-2 py-1 border rounded italic ${
                getInlineStyleState("ITALIC") === "active"
                  ? "bg-[#f26522] text-white"
                  : getInlineStyleState("ITALIC") === "mixed"
                  ? "bg-yellow-300 text-black"
                  : "bg-gray-100 hover:bg-gray-200"
              }`}
              aria-label="Italic"
            >
              I
            </button>
            <button
              type="button"
              onClick={() => onFormatClick("UNDERLINE")}
              className={`px-2 py-1 border rounded underline ${
                getInlineStyleState("UNDERLINE") === "active"
                  ? "bg-[#f26522] text-white"
                  : getInlineStyleState("UNDERLINE") === "mixed"
                  ? "bg-yellow-300 text-black"
                  : "bg-gray-100 hover:bg-gray-200"
              }`}
              aria-label="Underline"
            >
              U
            </button>
          </div>
          <div
            className={`border rounded bg-white min-h-[200px] p-4 min-w-0 max-w-full box-border poem-align-${form.alignment} font-serif`}
          >
            {editorMounted ? (
            <Editor
              key={form.alignment}
              editorState={editorState}
              onChange={setEditorState}
              handleKeyCommand={handleKeyCommand}
              textAlignment={
                form.alignment === "justify" ? "left" : form.alignment
              }
              placeholder="Your poem..."
              blockStyleFn={() => `align-${form.alignment}`}
              onFocus={() => setEditorFocused(true)}
              onBlur={() => {
                setEditorFocused(false);
                // Collapse selection to end to clear style state
                const selection = editorState.getSelection();
                const content = editorState.getCurrentContent();
                const lastBlock = content.getLastBlock();
                const lastKey = lastBlock.getKey();
                const lastLength = lastBlock.getLength();
                setEditorState(
                  EditorState.forceSelection(
                    editorState,
                    SelectionState.createEmpty(lastKey).merge({
                      anchorOffset: lastLength,
                      focusOffset: lastLength,
                    })
                  )
                );
              }}
            />
            ) : (
              <div className="min-h-[168px] flex items-center justify-center text-sm text-[#777777]">
                Loading editor...
              </div>
            )}
          </div>
        </div>
        <div className="relative">
          <label className="block font-medium mb-1">Tags (optional)</label>
          <input
            type="text"
            name="tags"
            placeholder="e.g. love, poetry, nature"
            value={form.tags}
            onChange={handleChange}
            onFocus={() => setTagInputFocused(true)}
            onBlur={() => {
              // Delay hiding suggestions to allow clicks
              setTimeout(() => {
                setTagInputFocused(false);
                setShowTagSuggestions(false);
              }, 300);
            }}
            className={`w-full min-w-0 max-w-full box-border p-2 border rounded ${
              formErrors.tags ? "border-red-500" : ""
            }`}
          />
          {showTagSuggestions &&
            tagInputFocused &&
            tagSuggestions.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-40 overflow-y-auto">
                {tagSuggestions.map((tagData, index) => (
                  <div
                    key={index}
                    className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm flex items-center justify-between"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleTagSuggestionClick(tagData.tag)}
                  >
                    <span className="text-gray-800">#{tagData.tag}</span>
                    <span className="text-gray-400 text-xs">
                      {tagData.count} poem{tagData.count !== 1 ? "s" : ""}
                    </span>
                  </div>
                ))}
              </div>
            )}
          <p className="text-gray-500 text-xs mt-1">
            Add hashtags to help others discover your poem. Separate multiple
            tags with commas. (Maximum 5 tags)
          </p>
          {formErrors.tags && (
            <p className="text-red-500 text-xs mt-1">{formErrors.tags}</p>
          )}
        </div>
        {/* Collaboration (optional) */}
        <div>
          <label className="block font-medium mb-1">Collaborate (optional)</label>
          <p className="text-xs text-gray-500 mb-2">Maximum 1 collaborator per poem</p>
          <div className="poem-compose-row">
            <input
              type="text"
              placeholder="Invite by pen name"
              value={inviteUsername}
              onChange={(e) => {
                const v = e.target.value;
                setInviteUsername(v);
                if (collabDebounce) clearTimeout(collabDebounce);
                setCollabDebounce(
                  setTimeout(async () => {
                    const q = v.trim();
                    if (!q) {
                      setCollabSuggestions([]);
                      setShowCollabSuggestions(false);
                      return;
                    }
                    try {
                      const res = await fetch(
                        getApiUrl(`/api/user/search?q=${encodeURIComponent(q)}`),
                        {
                          credentials: "include", // Include cookies for authentication
                        }
                      );
                      const users = (await res.json()) || [];
                      setCollabSuggestions(users.slice(0, 6));
                      setShowCollabSuggestions(true);
                    } catch {
                      setCollabSuggestions([]);
                      setShowCollabSuggestions(false);
                    }
                  }, 200)
                );
              }}
              className="flex-1 min-w-0 max-w-full box-border p-2 border rounded"
            />
            <button
              type="button"
              className="shrink-0 px-3 py-2 bg-[#f26522] text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={pendingInvites.length >= 1}
              onClick={() => {
                const u = inviteUsername.trim();
                if (!u) return;
                if (pendingInvites.length >= 1) return; // Limit to 1 collaborator
                if (!pendingInvites.includes(u)) {
                  setPendingInvites([u]); // Replace array with single username
                }
                setInviteUsername("");
              }}
            >
              Add
            </button>
          </div>
          {showCollabSuggestions && collabSuggestions.length > 0 && (
            <ul className="mt-1 border rounded bg-white shadow max-h-40 overflow-y-auto">
              {collabSuggestions.map((u) => (
                <li
                  key={u._id}
                  className="px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer flex items-center gap-2"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    if (pendingInvites.length >= 1) return; // Limit to 1 collaborator
                    if (!pendingInvites.includes(u.username)) {
                      setPendingInvites([u.username]); // Replace array with single username
                    }
                    setInviteUsername("");
                    setShowCollabSuggestions(false);
                  }}
                >
                  <img
                    src={getAvatarUrl(u.avatar)}
                    alt="avatar"
                    className="w-6 h-6 rounded-full object-cover"
                    onError={(e) => {
                      e.currentTarget.src =
                        e.currentTarget.src === DEFAULT_AVATAR_DATA_URL
                          ? FALLBACK_AVATAR_SVG
                          : DEFAULT_AVATAR_DATA_URL;
                    }}
                  />
                  <span className="font-medium">@{u.username}</span>
                  {u.name && <span className="text-gray-400">({u.name})</span>}
                </li>
              ))}
            </ul>
          )}
          {pendingInvites.length > 0 && (
            <div className="mt-2 text-sm text-gray-700">
              <div className="font-semibold">Will invite on post:</div>
              <ul className="flex flex-wrap gap-2 mt-1">
                {pendingInvites.map((u) => (
                  <li key={u} className="px-2 py-0.5 bg-gray-100 rounded flex items-center gap-1">
                    <span>@{u}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setPendingInvites((prev) => prev.filter((username) => username !== u));
                      }}
                      className="text-red-500 hover:text-red-700 focus:outline-none ml-1 flex items-center justify-center"
                      aria-label={`Remove ${u}`}
                      title="Remove collaborator"
                    >
                      <FaTimes className="w-3 h-3" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="rounded-xl border border-[#eeeeee] bg-white p-3">
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              className="mt-1"
              checked={!!form.copyrightEnabled}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, copyrightEnabled: e.target.checked }))
              }
            />
            <span className="min-w-0">
              <span className="block font-medium text-[#333333]">
                Add copyright notice
              </span>
              <span className="block text-xs text-[#777777] leading-relaxed">
                Displays as: © {new Date().getFullYear()}{" "}
                {(user?.username || "PeerPoetry").trim()}
              </span>
            </span>
          </label>
        </div>
        {isUploading && (
          <UploadProgressBar
            progress={uploadProgress}
            label={
              coverImageFile
                ? "Uploading poem and cover image..."
                : "Uploading poem..."
            }
          />
        )}
        <div className="flex flex-wrap gap-2 min-w-0">
          <button
            type="button"
            onClick={handleSaveDraft}
            disabled={isUploading}
            className="flex-1 min-w-[8.5rem] bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUploading ? "Saving..." : "Save as Draft"}
          </button>
          <button
            type="submit"
            disabled={!!groupAccessError || isUploading}
            className="flex-1 min-w-[8.5rem] bg-[#f26522] text-white py-2 rounded hover:opacity-90 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUploading
              ? "Uploading..."
              : groupInfo
              ? "Publish to group"
              : "Post Poem"}
          </button>
        </div>
      </form>
      )}
      <CoverImageCropModal
        open={isCropOpen}
        imageSrc={cropImageSrc}
        title="Crop poem cover image"
        fileNamePrefix="poem_cover"
        onCancel={handleCoverCropCancel}
        onComplete={handleCoverCropComplete}
      />
    </div>
  );
}
